import React, { useEffect, useMemo, useState, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './PaymentBreakdownCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import {
  ChartComponent,
  IFText,
  IFSkeleton,
  IFSegmentedControl,
} from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { useTranslation } from 'react-i18next'
import { useApexResizeObserver } from 'Utils/Resizeobserver'
import { getMoneyUnit } from 'Utils/Calculations'
const getMaxStackedValue = (series) => {
  if (!series.length) return 0

  const pointsCount = series[0].data.length

  let max = 0
  for (let i = 0; i < pointsCount; i++) {
    const total = series.reduce((sum, s) => sum + (Number(s.data[i]) || 0), 0)
    max = Math.max(max, total)
  }

  return max
}
const PaymentBreakdownCard = ({
  paymentBreakdown,
  fetchPaymentBreakdown,
  fetchPaymentBreakdownRequestState,
  filter,
  currency,
}) => {
  const { t } = useTranslation()
  const chartWrapperRef = useApexResizeObserver()

  useEffect(() => {
    fetchPaymentBreakdown(filter)
  }, [])

  useEffect(() => {
    fetchPaymentBreakdown(filter)
  }, [filter])
  const [segmentedState, setSegmentedState] = useState(t('Analytics.Count'))

  const { seriesCount, optionsCount, seriesAmount, optionsAmount, hasNoData } =
    useMemo(() => {
      const countGraph = paymentBreakdown?.data?.countGraph || {}
      const amountGraph = paymentBreakdown?.data?.amountGraph || {}

      const labels = Object.keys(countGraph)

      const STATUSES = ['success', 'pending', 'failed']

      const seriesCount = STATUSES.map((status) => ({
        name: t(`Analytics.${status}`),
        type: 'column',
        data: labels.map((label) => countGraph[label]?.[status] ?? 0),
      }))

      const seriesAmount = STATUSES.map((status) => ({
        name: t(`Analytics.${status}`),
        type: 'column',
        data: labels.map((label) => amountGraph[label]?.[status] ?? 0),
      }))

      const activeSeries =
        segmentedState === t('Analytics.Count') ? seriesCount : seriesAmount

      const hasNoData =
        activeSeries.length === 0 ||
        activeSeries.every(
          (s) =>
            !s.data ||
            s.data.length === 0 ||
            s.data.every((v) => Number(v) === 0),
        )

      const baseOptions = {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            columnWidth: '40%',
            borderRadius: 4,
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          width: 1,
          colors: ['#fff'],
        },
        xaxis: {
          categories: labels,
          labels: {
            style: { colors: Colors.grey500 },
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center',
        },
      }

      const maxCount = getMaxStackedValue(seriesCount)

      const optionsCount = {
        ...baseOptions,
        yaxis: {
          min: 0,
          max: maxCount,
          title: { text: t('Analytics.Count') },
          labels: {
            formatter: (val) => Math.round(val),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) => Math.round(val),
          },
        },
        colors: [
          Colors.primary,
          Colors.Analytics.Icons,
          Colors.Analytics.PeakHour.Shade1,
        ],
      }

      const maxAmount = getMaxStackedValue(seriesAmount)
      const { divisor: amountDivisor, unit: amountUnit } =
        getMoneyUnit(maxAmount)
      const optionsAmount = {
        ...baseOptions,
        yaxis: {
          min: 0,
          max: maxAmount,
          title: { text: t('Analytics.Amount') },
          labels: {
            formatter: (val) =>
              `${Math.round(val / amountDivisor)}${amountUnit} ${currency}`,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) =>
              `${(
                val / amountDivisor
              ).toLocaleString()}${amountUnit} ${currency}`,
          },
        },
        colors: [
          Colors.primary,
          Colors.Analytics.Icons,
          Colors.Analytics.PeakHour.Shade1,
        ],
      }
      return {
        seriesCount,
        optionsCount,
        seriesAmount,
        optionsAmount,
        hasNoData,
      }
    }, [paymentBreakdown, segmentedState])

  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      <div className={Styles.GraphContainer}>
        <div className={Styles.Segment}>
          <IFSegmentedControl
            name="group-1"
            isLoading={
              fetchPaymentBreakdownRequestState === RequestState.LOADING
            }
            callback={(val) => {
              setSegmentedState(val)
            }}
            controlRef={useRef()}
            segments={[
              {
                label: t('Analytics.Count'),
                value: t('Analytics.Count'),
                ref: useRef(),
              },
              {
                label: t('Analytics.Amount'),
                value: t('Analytics.Amount'),
                ref: useRef(),
              },
            ]}
          />
        </div>
        {fetchPaymentBreakdownRequestState === RequestState.LOADING ? (
          <div className={Styles.ChartSkeleton}>
            <IFSkeleton width="100%" height="100%" variant="rectangular" />
          </div>
        ) : (
          <div className={Styles.Chart} ref={chartWrapperRef}>
            <ChartComponent
              series={
                segmentedState === t('Analytics.Count')
                  ? seriesCount
                  : seriesAmount
              }
              options={
                segmentedState === t('Analytics.Count')
                  ? optionsCount
                  : optionsAmount
              }
              height="100%"
              width="100%"
              type="bar"
              className={
                (hasNoData ||
                  !(
                    fetchPaymentBreakdownRequestState ===
                      RequestState.LOADING ||
                    fetchPaymentBreakdownRequestState ===
                      RequestState.SUCCEEDED ||
                    fetchPaymentBreakdownRequestState ===
                      RequestState.UNINITIALIZED
                  )) &&
                Styles.OverlayTest
              }
            />

            {(hasNoData ||
              !(
                fetchPaymentBreakdownRequestState === RequestState.LOADING ||
                fetchPaymentBreakdownRequestState === RequestState.SUCCEEDED ||
                fetchPaymentBreakdownRequestState === RequestState.UNINITIALIZED
              )) && (
              <IFText
                className={Styles.OverlayText}
                style={{ color: Colors.red }}
              >
                {t('Analytics.NoData')}
              </IFText>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  paymentBreakdown: AnalyticsSelectors.getPaymentBreakdown(state),
  fetchPaymentBreakdownRequestState:
    AnalyticsSelectors.getPaymentBreakdownRequestState(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchPaymentBreakdown: (filter) =>
      dispatch(AnalyticsActions.fetchPaymentBreakdown(filter)),
  }
}

PaymentBreakdownCard.propTypes = {
  filter: PropTypes.object,
  fetchPaymentBreakdown: PropTypes.func,
  fetchPaymentBreakdownRequestState: PropTypes.number,
  paymentBreakdown: PropTypes.object,
  currency: PropTypes.string,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PaymentBreakdownCard)
